import type { IPlaybackScheduleRoot } from '@lucimark/shared/models/player-registration-payload'
import type { IPositionMeasurement } from '@lucimark/shared/models/geolocation'
import type { TGeoLatLng } from '@lucimark/shared/models/playlist-assignment-geo-filter'
import {
  maxFrameGeoFilterRadiusMeters,
  playbackScheduleHasFrameGeoFilter
} from '@lucimark/shared/helpers/frameGeoFilter'
import {
  maxGeoFilterRadiusMeters,
  resolvePlaybackAssignmentGeoPoint
} from '@lucimark/shared/helpers/playlistAssignmentGeoFilter'

/** True when any playlist row or frame on the schedule carries a geofilter. */
export function playbackScheduleHasGeoFilter(root: IPlaybackScheduleRoot | null | undefined): boolean {
  if ((root?.playlists ?? []).some(pl => pl.geoFilter != null)) {
    return true
  }
  return playbackScheduleHasFrameGeoFilter(root)
}

/** First stamped venue fallback from a geo-filtered row (same player venue for all). */
export function geoFallbackPointFromPlaybackSchedule(
  root: IPlaybackScheduleRoot | null | undefined
): TGeoLatLng | null {
  for (const pl of root?.playlists ?? []) {
    if (pl.geoFallbackPoint != null) {
      return pl.geoFallbackPoint
    }
  }
  return null
}

/** Largest geofilter radius on the schedule (GPS accuracy ceiling). */
export function maxGeoFilterRadiusFromPlaybackSchedule(
  root: IPlaybackScheduleRoot | null | undefined
): number | null {
  let max: number | null = null
  for (const pl of root?.playlists ?? []) {
    const r = maxGeoFilterRadiusMeters(pl.geoFilter ?? null)
    if (r == null) {
      continue
    }
    if (max == null || r > max) {
      max = r
    }
  }
  const frameMax = maxFrameGeoFilterRadiusMeters(root)
  if (frameMax != null && (max == null || frameMax > max)) {
    max = frameMax
  }
  return max
}

/** Live GPS → stamped venue fallback for playback geofilter evaluation (accuracy-aware). */
export function resolvePlaybackGeoPointForSchedule(
  position: IPositionMeasurement | null | undefined,
  root: IPlaybackScheduleRoot | null | undefined
): TGeoLatLng | null {
  return resolvePlaybackAssignmentGeoPoint({
    playerPosition: position,
    geoFallbackPoint: geoFallbackPointFromPlaybackSchedule(root),
    maxGpsAccuracyMeters: maxGeoFilterRadiusFromPlaybackSchedule(root)
  })
}
